/**
 *  Bundle Side Cart
 *  Dedicated cart drawer for the pages targeted in the "Bundle Side Cart"
 *  section. Takes over the theme cart drawer on those pages: the header cart
 *  icon and every call to #Cart-Drawer.open() open this drawer instead.
 */
(function () {
  if (customElements.get('bundle-side-cart')) return;

  var routes = (window.theme && window.theme.routes) || {};
  var ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  var CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  var CART_URL = (routes.cart_url || '/cart') + '.js';
  var UPDATE_URL = (routes.cart_url || '/cart') + '/update.js';

  function current() {
    return document.querySelector('bundle-side-cart');
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) throw data;
        return data;
      });
    });
  }

  function parse(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector);
  }

  class BundleSideCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.panel = this.querySelector('.bsc__panel');
      this.errorEl = this.querySelector('[data-bsc-error]');
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      patchThemeDrawer();
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      this.classList.add('is-open');
      document.body.classList.add('bundle-side-cart-open');
      document.body.classList.remove('open-cc');
      var themeDrawer = document.getElementById('Cart-Drawer');
      if (themeDrawer) themeDrawer.classList.remove('active');
      this.panel.focus({ preventScroll: true });
    }

    close() {
      var wasOpen = this.isOpen();
      this.classList.remove('is-open');
      document.body.classList.remove('bundle-side-cart-open');
      this.showError('');
      // The Build your bundle section listens for this to take its bundle back out of the cart.
      if (wasOpen && !(window.Shopify && window.Shopify.designMode)) {
        document.dispatchEvent(new CustomEvent('bundle-side-cart:close'));
      }
    }

    sectionsParam() {
      return this.sectionId + ',cart-bubble';
    }

    refresh() {
      var url = window.location.pathname + '?sections=' + encodeURIComponent(this.sectionsParam());
      return fetch(url)
        .then(function (response) { return response.json(); })
        .then(this.render.bind(this))
        .catch(function (error) { console.error(error); });
    }

    render(sections) {
      if (!sections) return;
      var html = sections[this.sectionId];
      if (html) {
        var fresh = parse(html, '[data-bsc-inner]');
        var inner = this.querySelector('[data-bsc-inner]');
        if (fresh && inner) inner.innerHTML = fresh.innerHTML;
      }
      if (sections['cart-bubble']) {
        var count = parse(sections['cart-bubble'], '.thb-item-count');
        if (count) {
          document.querySelectorAll('[id="cart-drawer-toggle"] .thb-item-count').forEach(function (el) {
            el.innerHTML = count.innerHTML;
          });
        }
      }
    }

    setLoading(state) {
      this.panel.classList.toggle('is-loading', state);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    // Bundle packs are discounted at checkout by the "Bundle Savings" automatic discount (Aniwell Bundle
    // Discount app), which only touches lines carrying _aw_bundle. Older bundle lines still list a pack
    // code in _aw_bundle_tiers ("3:33.32:CODE|5:39.98:CODE"); take those codes off the cart so they never
    // discount the whole cart again. Codes from anywhere else are kept. Resolves true when the cart's
    // codes changed.
    syncBundleCodes() {
      return fetch(CART_URL, { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          var packCodes = {};
          (cart.items || []).forEach(function (line) {
            var p = line.properties || {};
            if (!p._aw_bundle) return;
            if (p._aw_bundle_code) packCodes[String(p._aw_bundle_code).trim().toUpperCase()] = true;
            String(p._aw_bundle_tiers || '').split('|').forEach(function (entry) {
              var code = String(entry.split(':')[2] || '').trim();
              if (code) packCodes[code.toUpperCase()] = true;
            });
          });
          if (!Object.keys(packCodes).length) return false;

          var current = (cart.discount_codes || []).map(function (d) { return d.code; }).filter(Boolean);
          var next = current.filter(function (c) { return !packCodes[c.toUpperCase()]; });
          var upper = function (list) { return list.map(function (c) { return c.toUpperCase(); }).sort().join(','); };
          if (upper(next) === upper(current)) return false;
          return postJSON(UPDATE_URL, { discount: next.join(',') }).then(function () { return true; });
        })
        .catch(function (error) {
          console.error(error);
          return false;
        });
    }

    // Runs a cart request and re-renders from the sections returned with it.
    request(url, body) {
      var self = this;
      body.sections = this.sectionsParam();
      body.sections_url = window.location.pathname;
      this.setLoading(true);
      this.showError('');
      return postJSON(url, body)
        .then(function (data) {
          return self.syncBundleCodes().then(function (changed) {
            if (data.sections && !changed) self.render(data.sections);
            else return self.refresh();
          });
        })
        .catch(function (error) {
          self.showError(error.description || error.message || 'Something went wrong. Please try again.');
          return self.refresh();
        })
        .finally(function () {
          self.setLoading(false);
        });
    }

    onClick(event) {
      if (event.target.closest('[data-bsc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var remove = event.target.closest('[data-bsc-remove]');
      if (remove) {
        event.preventDefault();
        this.request(CHANGE_URL, { id: remove.dataset.key, quantity: 0 });
        return;
      }

      var add = event.target.closest('[data-bsc-upsell-add]');
      if (add) {
        event.preventDefault();
        var card = add.closest('[data-bsc-upsell]');
        var select = card && card.querySelector('[data-bsc-upsell-variant]');
        var variantId = Number(select ? select.value : add.dataset.variantId);
        this.request(ADD_URL, { items: [{ id: variantId, quantity: 1 }] });
      }
    }

    onChange(event) {
      var select = event.target;

      if (select.matches('[data-bsc-upsell-variant]')) {
        var option = select.options[select.selectedIndex];
        var price = select.closest('[data-bsc-upsell]').querySelector('.bsc-upsell__price');
        if (option && price && option.dataset.price) price.textContent = option.dataset.price;
        return;
      }

      if (select.matches('[data-bsc-variant]')) this.swapVariant(select);
    }

    // Shopify can't change a line's variant, so remove the line and add the
    // new variant with the same quantity, selling plan and properties. If the
    // add fails (e.g. sold out), the original variant is put back.
    swapVariant(select) {
      var self = this;
      var properties = {};
      try {
        properties = JSON.parse(select.dataset.properties || '{}') || {};
      } catch (e) {
        properties = {};
      }

      function lineFor(variantId) {
        var line = { id: Number(variantId), quantity: Number(select.dataset.qty) || 1 };
        if (select.dataset.sellingPlan) line.selling_plan = Number(select.dataset.sellingPlan);
        if (Object.keys(properties).length) line.properties = properties;
        return line;
      }

      this.setLoading(true);
      this.showError('');
      postJSON(CHANGE_URL, { id: select.dataset.key, quantity: 0 })
        .then(function () {
          return self.request(ADD_URL, { items: [lineFor(select.value)] }).then(function () {
            if (!self.errorEl || self.errorEl.hidden) return;
            var message = self.errorEl.textContent;
            return self.request(ADD_URL, { items: [lineFor(select.dataset.current)] }).then(function () {
              self.showError(message);
            });
          });
        })
        .catch(function (error) {
          self.showError(error.description || error.message || 'Something went wrong. Please try again.');
          self.refresh();
        })
        .finally(function () {
          self.setLoading(false);
        });
    }
  }

  customElements.define('bundle-side-cart', BundleSideCart);

  // Point the theme drawer's open() at this cart, so any theme add-to-cart
  // (bundle builder, quick add, product forms) opens the bundle cart instead.
  function patchThemeDrawer() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer || drawer._bscPatched) return;
    drawer._bscPatched = true;
    drawer.open = function () {
      var cart = current();
      if (!cart) return;
      cart.refresh().then(function () { cart.open(); });
    };
  }

  // Header cart icon: capture phase so the theme's own listener never runs.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[id="cart-drawer-toggle"]');
    var cart = current();
    if (!toggle || !cart) return;
    event.preventDefault();
    event.stopPropagation();
    cart.open();
    cart.refresh();
  }, true);

  document.addEventListener('keyup', function (event) {
    var cart = current();
    if (event.key === 'Escape' && cart && cart.isOpen()) cart.close();
  });

  document.addEventListener('cart:refresh', function () {
    var cart = current();
    if (cart) cart.refresh();
  });

  // Theme editor: open the drawer while its section or blocks are selected.
  if (window.Shopify && window.Shopify.designMode) {
    var matches = function (event) {
      var cart = current();
      return cart && event.detail && event.detail.sectionId === cart.sectionId ? cart : null;
    };
    ['shopify:section:select', 'shopify:section:load', 'shopify:block:select'].forEach(function (name) {
      document.addEventListener(name, function (event) {
        var cart = matches(event);
        if (cart) cart.open();
      });
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      var cart = matches(event);
      if (cart) cart.close();
    });
  }
})();
